import dynamic from "next/dynamic";
import { MarketingCenteredText } from "../../../components/MarketingCenteredTextBlock";
import { MarketingFooter } from "../../../components/MarketingFooter";
import { MarketingGetStarted } from "../../../components/MarketingGetStarted";
import { MarketingMenu } from "../../../components/MarketingMenu";
import { MarketingOGTags } from "../../../components/MarketingOGTags";
import { MarketingTextBlock } from "../../../components/MarketingTextBlock";
import { MarketingUseCaseHeader, WritersDesktop } from "../../../components/MarketingUseCaseHeader";
import { MarketingUserPledge } from "../../../components/MarketingUserPledge";
import { FadeUpContainer } from "../../../components/ReusableStyledComponents/FadeUp";
import { ScrollDownArrow } from "../../../components/ScrollDownArrow";
import { VerticalSpacing } from "../../../components/VerticalSpacing";
import { theme } from "../../../theme/theme";
import { APPLICATION_ROUTES } from "../../../shared-logic/routes";

const MarketingCarouselUseCases = dynamic(() => import("../../../components/MarketingCarouselUseCases/MarketingCarouselUseCases"), { ssr: false });

const urls = [
    "https://authory.com/AnneliseKelly",
    "https://authory.com/DannyAnderson",
    "https://authory.com/MattDonato",
    "https://authory.com/ErinRupp",
    "https://authory.com/CharlesMoss",
    "https://authory.com/IvanFernandez",
    "https://authory.com/BrianClegg",
    "https://authory.com/AlyJYale",
    "https://authory.com/BrandonHill",
    "https://authory.com/BenPopken"
];

export const WritersPageLayout = () => {
    return (
        <>
            <MarketingOGTags
                title="Authory – Get a writer portfolio like you've never seen before"
                description="Authory is the go-to portfolio solution for thousands of top writers around the world."
                canonicalUrl={`${process.env.NEXT_PUBLIC_FRONTEND_URL}${APPLICATION_ROUTES.USE_CASE_WRITERS}`}
            />
            <MarketingMenu />
            <FadeUpContainer>
                <ScrollDownArrow
                    content={
                        <VerticalSpacing top={0} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 20 }]}>
                            <MarketingUseCaseHeader
                                title="Writers"
                                description={
                                    <>
                                        <strong>“Authory is effortless.</strong> For less than the cost than running my own website I have a clean,
                                        up-to-date, searchable portfolio that I don't have to manage. Authory is like having my own mini marketing
                                        team that lets me focus on the work I love doing.”
                                    </>
                                }
                                desktopImage={<WritersDesktop />}
                                mobileImage={<></>}
                                userBio={
                                    <>
                                        <strong>- Colleen Fisher Tully</strong>, Writer
                                    </>
                                }
                                profileLink={"https://authory.com/ColleenFisherTully"}
                            />
                        </VerticalSpacing>
                    }
                />
                <VerticalSpacing top={80}>
                    <MarketingCarouselUseCases
                        imagePath="/authory_static_files/images/use-cases/slider/writers/writers_"
                        urls={urls}
                        targetAudience="writers"
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Portfolio"}
                        punchline={
                            <>
                                Get a portfolio like you've <strong>never seen before</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Authory gives you a portfolio page that is close to magical: It is automatically updating with your new pieces
                                    whenever you publish something. And it contains all your past articles, texts and anything else you might create,
                                    regardless of when and where they've been published.
                                </p>
                                <p>
                                    It's the only portfolio page that works like an interactive resume, showing the entire breadth and depth of your
                                    body of work. All while making sure your content looks beautiful right out of the box. There is no better way to
                                    make your work truly shine.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.PORTFOLIO}
                        linkText={
                            <>
                                Learn more about <strong>Portfolio</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/present_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/present_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        iconPosition="right"
                        punchline={
                            <>
                                Make sure your writing will <strong>never be lost again</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    There are countless reasons why publishers/websites take down your content. They decide older content is not
                                    needed anymore, merge, get bought, go out of business, and on a regular basis they botch a move to a new CMS
                                    losing countless articles in the process.{" "}
                                </p>
                                <p>
                                    Any one of these reasons is enough to warrant a backup of your articles. Authory does that for you: You'll get
                                    backup of everything you've ever written, and your new pieces will be backed up automatically too. For complete
                                    peace of mind.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/protect_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/protect_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingUserPledge
                        title={
                            <>
                                “Authory helps me rest easier in the knowledge that all my hard work{" "}
                                <strong>won't just disappear into the black hole of the internet.</strong>”
                            </>
                        }
                        description={
                            <>
                                <strong>T. Cole Rachel,</strong> Interview Magazine, The Fader and more
                            </>
                        }
                        avatarImage={"/authory_static_files/images/pledges/Cole.jpg"}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Archive"}
                        punchline={
                            <>
                                <strong>Bulletproof confirmation</strong> where you've published
                            </>
                        }
                        description={
                            <>
                                <p>
                                    {" "}
                                    Your portfolio is not just proof of the quality of the work you've done so far. It also shows where you've
                                    published your articles and texts. And that is just as important of a statement.
                                </p>
                                <p>
                                    With Authory, this proof of publication will be yours forever.If your articles have been deleted at the original
                                    source, you can make them accessible again to anybody. It just takes single click in your Authory account.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ARCHIVE}
                        linkText={
                            <>
                                Learn more about <strong>Archive</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/protect_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/protect_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Tracking"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Zero effort and zero tech skills</strong> required
                            </>
                        }
                        description={
                            <>
                                Setting up your Authory account takes about 3 minutes. And with that you don't need to do anything anymore. Authory
                                will automatically find all your past and your future work that's been published under your name. And adding anything
                                ghostwritten or without a byline at all takes a few second only. It could not be easier to finally track and have all
                                your content under one roof without having to worry about the upkeep.
                            </>
                        }
                        href={APPLICATION_ROUTES.TRACKING}
                        linkText={
                            <>
                                Learn more about <strong>Tracking</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/track_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/track_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Newsletter"}
                        punchline={
                            <>
                                Turn casual readers into <strong>loyal subscribers</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    On your Authory page, your readers can subscribe to your email list with a single click, no signup necessary. It's
                                    usually a pain to find the right mailing provider, build and host a signup form and, most importantly, regularly
                                    create a newsletter.{" "}
                                </p>
                                <p>
                                    Authory does all that for you and automatically sends your subscribers a well-designed email digest with your new
                                    pieces on a daily or weekly basis. That's a direct relationship with your readers and literally zero effort for
                                    you.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.NEWSLETTER_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Newsletter</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/distribute_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/distribute_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Analytics"}
                        iconPosition="right"
                        punchline={
                            <>
                                Find out how your articles <strong>perform on social media</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    There are plenty of ways to find out what your social media accounts' reach is. Yet, do you know what reach your
                                    content has? Authory will tell you exactly that: Regardless who shares, likes, comments, tweets or pins an article
                                    you've written, with Authory you'll know.
                                </p>
                                <p>
                                    You can check out your aggregate metrics, or you can drill them down and find out about the social media
                                    performance of individual content pieces anytime. And because Authory doesn't only monitor social media for your
                                    new pieces, but for your entire archive, you'll also know if older pieces of yours start trending again.
                                </p>
                            </>
                        }
                        href={APPLICATION_ROUTES.ANALYTICS_MARKETING}
                        linkText={
                            <>
                                Learn more about <strong>Analytics</strong>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/analyze_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/analyze_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingCenteredText
                        title={"Share"}
                        iconPosition="right"
                        punchline={
                            <>
                                <strong>Say Goodbye</strong> to bulky PDFs
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Intend to apply for a new job or gig? Forget the times when you had to frantically search for your previous
                                    articles matching the job description, turn them into bulky PDFs and hope for the best.{" "}
                                </p>
                                <p>
                                    {" "}
                                    With Authory you can create a Collection with selected content from your account in a few seconds, then share a
                                    special link to this Collection with anybody who is supposed to see it. No matter what device the recipient uses,
                                    your content will look brilliant. It's a different league from PDFs.
                                </p>
                            </>
                        }
                        desktopIcon={<img src="/authory_static_files/images/icons/share_xl_active.svg" />}
                        mobileIcon={<img src="/authory_static_files/images/icons/share_m_active.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120} breakpoints={[{ screen: theme.breakpoints.tablet, marginTop: 180 }]}>
                    <MarketingTextBlock
                        title={
                            <>
                                Your very own <strong>blog included</strong>
                            </>
                        }
                        description={
                            <>
                                <p>
                                    Sometimes you might not want to write somewhere else. You might want to publish on your own turf instead. Forget
                                    about having to set up a blog for this, or having to use a third party service with zero control like Medium.
                                </p>
                                <p>
                                    Authory comes with a built-in blogging feature, so you can publish your articles, pictures or videos right on your
                                    Authory page. Of course, out of the box it'll look as brilliant as everything else on your Authory page does.
                                </p>
                            </>
                        }
                        icon={<img src="/authory_static_files/images/icons/blog.svg" />}
                    />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingGetStarted />
                </VerticalSpacing>
                <VerticalSpacing top={120}>
                    <MarketingFooter />
                </VerticalSpacing>
            </FadeUpContainer>
        </>
    );
};
